import type { LexiconEntry } from './lexicon-schema.js'

/**
 * Function-Quiz v1 (Phase 2 Tier 1) lexicon entries — the three answer
 * categories + question prompt + session-end footer copy. Authored by
 * Bowie in the Function Quiz scope memo; locked verbatim — do not edit
 * without Bowie review.
 *
 * Reuses the existing `function-*` category for ID conventions and
 * category routing.
 *
 * Spec: docs/superpowers/specs/2026-05-18-function-quiz-design.md §11
 * Bowie scope memo: .claude/agent-memory/bowie/project_function_quiz_v1_scope.md
 */
export const FUNCTION_QUIZ_LEXICON_ENTRIES: LexiconEntry[] = [
  {
    id: 'function-category-tonic',
    category: 'function',
    displayName: 'Tonic',
    displayNameShort: 'T',
    pedagogicalLevel: 1,
    copy: {
      beginner: 'Home base — where the song rests.',
      intermediate: 'Tonic function — the point of repose and resolution.',
      advanced: 'Tonic function — the structural center; chords serving this role establish or confirm the key.',
    },
    tagLabel: { beginner: 'home', intermediate: 'tonic', advanced: 'T' },
  },
  {
    id: 'function-category-predominant',
    category: 'function',
    displayName: 'Predominant',
    displayNameShort: 'PD',
    pedagogicalLevel: 1,
    copy: {
      beginner: 'A stepping stone — leads toward the chord that pulls home.',
      intermediate: 'Predominant function — sets up the dominant.',
      advanced: 'Predominant function — chords (ii, IV, iv, ♭VI) that prepare dominant motion.',
    },
    tagLabel: { beginner: 'setup', intermediate: 'predominant', advanced: 'PD' },
  },
  {
    id: 'function-category-dominant',
    category: 'function',
    displayName: 'Dominant',
    displayNameShort: 'D',
    pedagogicalLevel: 1,
    copy: {
      beginner: 'The tension chord — pulls strongly back to home.',
      intermediate: 'Dominant function — strong pull toward tonic.',
      advanced: 'Dominant function — V or vii° creates the leading-tone tension that resolves to I.',
    },
    tagLabel: { beginner: 'tense', intermediate: 'dominant', advanced: 'D' },
  },
  {
    id: 'function-quiz-prompt',
    category: 'function',
    displayName: 'Function quiz question',
    displayNameShort: 'Question',
    pedagogicalLevel: 1,
    copy: {
      beginner: 'What does this chord do in the key?',
      intermediate: "What's this chord's function?",
      advanced: 'Identify the harmonic function.',
    },
    tagLabel: { beginner: 'what?', intermediate: 'function?', advanced: 'function' },
  },
  {
    id: 'function-quiz-feedback',
    category: 'function',
    displayName: 'Function quiz feedback',
    displayNameShort: 'Feedback',
    pedagogicalLevel: 1,
    copy: {
      beginner: 'Listen for what comes next — the chord after this one tells you its job.',
      intermediate: 'Function is about what comes next, not what the chord sounds like alone.',
      advanced: 'Function is contextual — defined by harmonic motion, not by chord quality in isolation.',
    },
    tagLabel: { beginner: 'next', intermediate: 'context', advanced: 'context' },
  },
  // Welcome-screen intro paragraph. Polish item 4/6 (2026-05-20) — migrated
  // from an inline string in `function-quiz.tsx` to honor the lexicon-as-
  // source-of-truth convention. The "10 questions per session" detail stays
  // inline (it's UI configuration, no register variance).
  {
    id: 'function-quiz-welcome-intro',
    category: 'function',
    displayName: 'Function quiz introduction',
    displayNameShort: 'Intro',
    pedagogicalLevel: 1,
    copy: {
      beginner: 'Listen and pick what each chord does in the song.',
      intermediate:
        "Hear a chord resolve to home — pick whether it's a Tonic, Predominant, or Dominant.",
      advanced:
        'Identify each chord\'s harmonic function — Tonic, Predominant, or Dominant — by hearing its resolution to tonic.',
    },
    tagLabel: { beginner: 'how it works', intermediate: 'how to play', advanced: 'methodology' },
  },
]
